// Live-ticking SLA timer for the ticket detail header.
//
// Picks the relevant deadline:
//   * If first_response_at is null → countdown to sla_first_response_due_at
//     (labelled "First reply").
//   * Else if status is open → countdown to sla_resolution_due_at
//     (labelled "Resolution").
//   * Else → renders nothing (closed/resolved tickets have no live SLA).
//
// Visual state:
//   * Green   — > 30 minutes left
//   * Amber   — ≤ 30 minutes left, not yet breached
//   * Rose    — already breached (overdue)
//
// Re-renders on a 30-second interval — fine-grained ticking would burn
// renders for no real benefit (humans don't notice a 30s delay on a
// minute-resolution timer).

import { useEffect, useMemo, useState } from 'react';
import { AlarmClock, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Ticket } from '@/lib/queries';

interface Props {
  ticket: Ticket;
}

const TICK_MS = 30_000;

export function SlaCountdown({ ticket }: Props) {
  // useEffect-driven re-render every 30s. Cheap; only this component
  // re-renders, and SlaCountdown is leaf-level.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), TICK_MS);
    return () => window.clearInterval(id);
  }, []);

  const view = useMemo(() => buildView(ticket), [
    ticket,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    Math.floor(Date.now() / TICK_MS), // re-derive each tick
  ]);

  if (!view) return null;

  return (
    <div
      role="status"
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-2xs font-medium',
        view.tone === 'rose' && 'border-tint-critical-ring bg-tint-critical text-tint-critical-fg',
        view.tone === 'amber' && 'border-tint-warning-ring bg-tint-warning text-tint-warning-fg',
        view.tone === 'emerald' && 'border-tint-success-ring bg-tint-success text-tint-success-fg',
      )}
      title={view.tooltip}
    >
      {view.tone === 'rose' ? (
        <AlarmClock className="h-3 w-3" />
      ) : (
        <Clock className="h-3 w-3" />
      )}
      <span>
        <span className="font-semibold">{view.kindLabel}</span>
        <span className="mx-1 opacity-60">·</span>
        {view.label}
      </span>
    </div>
  );
}

interface View {
  tone: 'rose' | 'amber' | 'emerald';
  kindLabel: 'First reply' | 'Resolution';
  label: string;
  tooltip: string;
}

function buildView(ticket: Ticket): View | null {
  const now = Date.now();
  const isClosed = ticket.status === 'resolved' || ticket.status === 'closed';

  // First-response phase: countdown until the first outbound message lands.
  if (!ticket.first_response_at && ticket.sla_first_response_due_at) {
    return formatPhase(
      'First reply',
      new Date(ticket.sla_first_response_due_at).getTime() - now,
      ticket.sla_first_response_due_at,
    );
  }
  // Resolution phase: only show while the ticket is still actually open.
  if (!isClosed && ticket.sla_resolution_due_at) {
    return formatPhase(
      'Resolution',
      new Date(ticket.sla_resolution_due_at).getTime() - now,
      ticket.sla_resolution_due_at,
    );
  }
  return null;
}

function formatPhase(
  kindLabel: View['kindLabel'],
  remainingMs: number,
  dueIso: string,
): View {
  const breached = remainingMs <= 0;
  const totalMin = Math.round(Math.abs(remainingMs) / 60_000);

  // Pick a tone band by signed minutes.
  const tone: View['tone'] = breached ? 'rose' : remainingMs < 30 * 60_000 ? 'amber' : 'emerald';

  const label = breached
    ? `breached ${humanizeMinutes(totalMin)} ago`
    : `${humanizeMinutes(totalMin)} left`;

  return {
    tone,
    kindLabel,
    label,
    tooltip: `${kindLabel} due at ${new Date(dueIso).toLocaleString()}`,
  };
}

function humanizeMinutes(m: number): string {
  if (m < 1) return '<1m';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  if (h < 24) return rem ? `${h}h ${rem}m` : `${h}h`;
  const d = Math.floor(h / 24);
  const remH = h % 24;
  return remH ? `${d}d ${remH}h` : `${d}d`;
}
